import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarClock, CheckCircle2, Contact, GitPullRequestArrow, Target, Warehouse } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { PROPERTY_TYPES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/PlaceholderPage";

const ALL = "__all__";

const typeParam = (t) => (t === ALL ? "" : `property_type=${encodeURIComponent(t)}`);

const STATS = [
  { key: "total_leads", label: "Total Leads", icon: Contact, to: (t) => `/leads?${typeParam(t)}` },
  { key: "total_properties", label: "Total Properties", icon: Warehouse, to: (t) => `/properties?${typeParam(t)}` },
  { key: "total_follow_up", label: "Total Follow Up", icon: CalendarClock, to: (t) => `/leads?status=${encodeURIComponent("Follow-Up Required")}&${typeParam(t)}` },
  { key: "total_pipeline", label: "Total Pipeline", icon: GitPullRequestArrow, to: () => "/deals" },
  { key: "total_closure", label: "Total Closure", icon: Target, to: () => "/deals" },
  { key: "total_deal_done", label: "Total Deal Done", icon: CheckCircle2, to: () => "/deals?tab=closed&outcome=Done" },
];

export default function DashboardPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === "admin";
  const [type, setType] = useState(ALL);

  const params = type === ALL ? {} : { property_type: type };
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", params],
    queryFn: async () => (await api.get("/dashboard", { params })).data,
  });

  return (
    <div className="p-6 sm:p-8" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle={
          isAdmin
            ? `Welcome back, ${currentUser?.full_name || ""} — office-wide overview`
            : "Showing your assigned leads and deals"
        }
      >
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="dashboard-type-filter" className="w-[200px] rounded-lg bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Property Types</SelectItem>
            {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {STATS.map(({ key, label, icon: Icon, to }) => (
          <button
            key={key}
            onClick={() => navigate(to(type))}
            data-testid={`stat-${key}`}
            className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-5 text-left hover:border-emerald-300 hover:-translate-y-[2px] hover:shadow-md transition-all duration-200"
          >
            <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
              <Icon className="h-4.5 w-4.5 text-emerald-800" strokeWidth={1.5} size={18} />
            </div>
            <p className="font-heading text-3xl font-bold text-emerald-950" data-testid={`stat-value-${key}`}>
              {isLoading || !data ? "…" : data[key]}
            </p>
            <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-6 sm:p-8 animate-fade-up" data-testid="phase-chart-card">
        <h2 className="font-heading text-lg font-semibold text-emerald-950 mb-6 pb-3 border-b border-slate-100">
          Deals ongoing — by phase
        </h2>
        {isLoading || !data ? (
          <p className="text-slate-400 text-sm py-10 text-center">Loading chart…</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_phase} layout="vertical" margin={{ left: 24, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" allowDecimals={false} tick={{ fill: "#64748b", fontSize: 12 }} />
              <YAxis type="category" dataKey="phase" width={110} tick={{ fill: "#334155", fontSize: 13 }} />
              <Tooltip cursor={{ fill: "rgba(6,78,59,0.05)" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Bar dataKey="count" name="Active deals" fill="#064e3b" radius={[0, 6, 6, 0]} barSize={26} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
